import { makeAuthenticatedGetRequest, makeGetRequest } from '../axios';
import {
  researchHubContentFeed,
  researchHubContentItem,
  researchHubContentOpen,
} from '../axios/endpoints';

export type ContentKind = 'article' | 'research_update' | 'market_update';
export type ContentLane = 'open' | 'gated';

export type ContentVisibility = 'both' | 'public_only' | 'subscriber_only' | 'platform_only' | null;

export type LockReason =
  | 'subscriber_only_feature_missing'
  | 'compass_project_type_not_accessible'
  | null;

export interface ContentMedia {
  url: string | null;
  alternativeText: string | null;
  width: number | null;
  height: number | null;
  formats: unknown;
}

export interface ContentPerson {
  name: string;
  title: string;
}

/** Card metadata for all three kinds: a payload carries only the keys its own kind has. */
export interface ContentMeta {
  strapi_id?: number | null;
  sort_index?: number;
  cover?: ContentMedia | null;
  image?: ContentMedia | null;
  author?: ContentPerson | null;
  post_category?: { display_name: string; url_slug: string } | null;
  featured_summary?: string | null;
  is_featured?: 'featured_one' | 'featured_two' | null;
  display_priority?: 'one' | 'two' | 'three' | 'none' | null;
  is_pinned_related_content?: boolean;
  content_detail_alternate_url?: string | null;
  hubspot_form_id?: string | null;
  website_home_news_insights_placement?: string | null;
  resource_file?: ContentMedia | null;
  next_up_slugs?: string[];
  preview?: string | null;
  areas_covered?: string[];
  takeaway?: string | null;
  is_corsia?: boolean;
  source_links?: { header: string; url: string }[];
  updated_at?: string | null;
}

export interface ContentItem {
  kind: ContentKind;
  lane: ContentLane;
  slug: string;
  external_id: string;
  title: string;
  visibility: ContentVisibility;
  published_date: string | null;
  teaser: string;
  project_types: string[];
  locked: boolean;
  lock_reason: LockReason;
  body: string | null;
  meta: ContentMeta;
}

interface Envelope<T> {
  statusCode: number;
  message: string;
  data: T;
}

interface OpenLanePayload {
  version: number;
  channel: string;
  count: number;
  includes_body: boolean;
  items: ContentItem[];
}

interface FeedPayload {
  version: number | null;
  source: 'snapshot' | 'strapi_draft';
  count: number;
  items: ContentItem[];
}

interface ItemPayload {
  version: number | null;
  source: 'snapshot' | 'strapi_draft';
  item: ContentItem;
}

/** The backend wraps every 2xx in TransformInterceptor, so the payload is res.data.data. */
const unwrap = <T>(res: { data: Envelope<T> }): T => res.data.data;

/** The open lane: no gated row, and no credential, so a caller without the hub feature can
 * still be shown public cards. */
export const fetchOpenLane = async (includeBody = false): Promise<ContentItem[]> => {
  const res = await makeGetRequest<Envelope<OpenLanePayload>>(researchHubContentOpen, {
    params: includeBody ? { include: 'body' } : undefined,
  });
  return unwrap(res).items ?? [];
};

/** Every row the caller may see as a card. Gated ones they cannot read arrive locked, with
 * no body. No parameter here widens that: the backend resolves the plan. */
export const fetchFeed = async (kind: ContentKind): Promise<ContentItem[]> => {
  const res = await makeAuthenticatedGetRequest<Envelope<FeedPayload>>(researchHubContentFeed, {
    params: { kind },
  });
  return unwrap(res).items ?? [];
};

/** One entry with its body, when the caller is entitled to it. */
export const fetchContentItem = async (
  slug: string,
  kind: ContentKind
): Promise<ContentItem | undefined> => {
  const res = await makeAuthenticatedGetRequest<Envelope<ItemPayload>>(
    `${researchHubContentItem}/${encodeURIComponent(slug)}`,
    { params: { kind } }
  );
  return unwrap(res).item;
};
